import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CountryISO,
  NgxIntlTelInputModule,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input-gg';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DialogModule } from 'primeng/dialog';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { Observable, catchError, concatMap, last, lastValueFrom, of, tap } from 'rxjs';
import { ClinicsTableComponent } from 'src/app/components/tables/clinic/clinic-table.component';
import { Clinic } from 'src/app/models/clinic.model';
import { PhoneNumber } from 'src/app/models/phone.model';
import { ClinicService } from 'src/app/services/clinic.service';
import { SideBarMenuService } from 'src/app/services/sidebar-menu.service';
import { generatePreview, generateSlug, validateImage } from 'src/app/services/utils.service';
@Component({
  selector: 'app-clinics',
  standalone: true,
  imports: [
    BreadcrumbModule,
    AsyncPipe,
    ClinicsTableComponent,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToastModule,
    InputTextareaModule,
    FileUploadModule,
    CommonModule,
    ImageModule,
    ColorPickerModule,
    NgxIntlTelInputModule,
  ],
  templateUrl: './clinics.component.html',
  providers: [ClinicService, MessageService],
})
export class ClinicsComponent implements OnInit {
  appName: string = localStorage.getItem('appName');
  home: MenuItem;
  breadcrumbItems: MenuItem[] = [];

  clinics$: Observable<Clinic[]>;
  clinicId: string;

  selectedFile: File | null;
  previewUrl: string | ArrayBuffer | null = null;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  onlyCountries: CountryISO[] = [CountryISO.Spain];

  private clinicService = inject(ClinicService);
  private sidebarMenuService = inject(SideBarMenuService);
  private afs = inject(AngularFirestore);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private storage = inject(AngularFireStorage);

  viewModal: boolean = false;
  loading: boolean = false;

  form = this.fb.group({
    name: ['', [Validators.required]],
    phone: [null as PhoneNumber | null, [Validators.required]],
    privacyLink: ['', [Validators.required]],
    color: [''],
    fileImage: new FormControl(null, [validateImage]),
  });

  ngOnInit(): void {
    this.home = {
      icon: 'pi pi-fw pi-mobile',
      routerLink: '/',
      command: () => this.sidebarMenuService.clearSessionStorage(),
    };
    this.breadcrumbItems = [
      { label: this.appName },
      { label: 'Clínicas', styleClass: 'font-bold' },
    ];

    this.clinics$ = this.clinicService.findClinics();
    this.clinicId = this.afs.createId();
  }

  add() {
    this.viewModal = true;
  }

  async onSubmit() {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }
    const val = this.form.value;

    const newClinic: Partial<Clinic> = {
      name: val.name,
      phone: val.phone.e164Number,
      slug: generateSlug(val.name),
      privacyLink: val.privacyLink,
    };

    if (val.color) {
      newClinic.color = val.color;
    }

    if (this.selectedFile) {
      const filePath = `clinics/${this.clinicId}/logo`;
      const task = this.storage.upload(filePath, this.selectedFile);
      await lastValueFrom(
        task.snapshotChanges().pipe(
          last(),
          concatMap(() => this.storage.ref(filePath).getDownloadURL()),
          tap((url) => (this.previewUrl = url)),
          catchError((err) => {
            console.log(err);
            alert('dio error');
            return of(null);
          }),
        ),
      );
      newClinic.image = this.previewUrl as string;
      this.createClinic(newClinic);
      return;
    }

    this.createClinic(newClinic);
  }

  private createClinic(newClinic: Partial<Clinic>) {
    this.clinicService
      .createClinic(newClinic, this.clinicId)
      .pipe(
        tap((clinic) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Nueva Clínica agregada`,
            life: 3000,
          });
          this.viewModal = false;
          this.clinicId = this.afs.createId();
          this.form.reset();
        }),
        catchError((error) => {
          console.error('Error al crear ', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  onUpload(event: FileUploadHandlerEvent) {
    this.form.patchValue({ fileImage: event.files[0] });
    this.selectedFile = event.files[0];
    generatePreview(this.selectedFile).subscribe({
      next: (previewUrl) => {
        this.previewUrl = previewUrl;
      },
    });
  }
}
